import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Calculator, Settings2, TrendingUp, TrendingDown } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { VehiclePicker } from '@/components/common/VehiclePicker';

// PR-F1 (Step 4) — "Will it pay?" cost-to-serve estimator. Pick the truck,
// enter the route km and the offered price; see diesel + driver + wasika +
// tasreeh vs the price, and the margin, BEFORE taking the load.

interface Estimate {
  km: number;
  breakdown: { dieselSar: number | null; driverSar: number; driverPerKmSar: number; wasikaSar: number; tasreehSar: number };
  totalCostSar: number | null;
  offeredPriceSar: number | null;
  marginSar: number | null;
  marginPct: number | null;
  worthIt: boolean | null;
  dieselConfidence: string;
  dieselSource: string;
  dieselNote: string | null;
}
interface Config { driverPerKmSar: number; wasikaSar: number; tasreehSar: number }

const inputCls = 'h-10 w-full rounded-md border border-input bg-background px-3 text-sm';
const sar = (n: number | null) => (n != null ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—');

export default function FlatbedEstimatePage() {
  const qc = useQueryClient();
  const [vehicleId, setVehicleId] = useState<string | null>(null);
  const [km, setKm] = useState('');
  const [offered, setOffered] = useState('');

  const { data: est, isFetching } = useQuery({
    queryKey: ['flatbed-estimate', vehicleId, km, offered],
    enabled: !!vehicleId && Number(km) > 0,
    queryFn: async () => (await apiClient.get<Estimate>('/api/v1/flatbed-estimate/estimate', {
      params: { vehicleId, km, offeredPrice: offered || undefined },
    })).data,
  });

  // config
  const { data: cfg } = useQuery({
    queryKey: ['flatbed-cost-config'],
    queryFn: async () => (await apiClient.get<{ config: Config }>('/api/v1/flatbed-estimate/config')).data.config,
  });
  const [showCfg, setShowCfg] = useState(false);
  const [cDriver, setCDriver] = useState('');
  const [cWasika, setCWasika] = useState('');
  const [cTasreeh, setCTasreeh] = useState('');
  const saveCfg = useMutation({
    mutationFn: async () => apiClient.put('/api/v1/flatbed-estimate/config', {
      driverPerKmSar: Number(cDriver || 0), wasikaSar: Number(cWasika || 0), tasreehSar: Number(cTasreeh || 0),
    }),
    onSuccess: () => { setShowCfg(false); qc.invalidateQueries({ queryKey: ['flatbed-cost-config'] }); qc.invalidateQueries({ queryKey: ['flatbed-estimate'] }); },
  });
  function openCfg() {
    setCDriver(String(cfg?.driverPerKmSar ?? '')); setCWasika(String(cfg?.wasikaSar ?? '')); setCTasreeh(String(cfg?.tasreehSar ?? ''));
    setShowCfg(true);
  }

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="font-display text-lg">Flatbed · Will it pay?</div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={openCfg} className="gap-1"><Settings2 className="h-4 w-4" /> Default costs</Button>
            <Button variant="ghost" size="sm" onClick={() => { window.location.href = '/dashboard/flatbed'; }} className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Flatbed
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-8">
        {showCfg && (
          <div className="rounded-xl border border-border bg-card p-4 mb-6">
            <div className="text-sm font-medium mb-3">Default costs (used by the estimate)</div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs text-muted-foreground">Driver / km (SAR)</label><input className={inputCls} value={cDriver} onChange={(e) => setCDriver(e.target.value)} inputMode="decimal" /></div>
              <div><label className="text-xs text-muted-foreground">Wasika (SAR)</label><input className={inputCls} value={cWasika} onChange={(e) => setCWasika(e.target.value)} inputMode="decimal" /></div>
              <div><label className="text-xs text-muted-foreground">Tasreeh (SAR)</label><input className={inputCls} value={cTasreeh} onChange={(e) => setCTasreeh(e.target.value)} inputMode="decimal" /></div>
            </div>
            <div className="mt-3 flex gap-2"><Button size="sm" disabled={saveCfg.isPending} onClick={() => saveCfg.mutate()}>Save defaults</Button><Button size="sm" variant="ghost" onClick={() => setShowCfg(false)}>Cancel</Button></div>
          </div>
        )}

        <div className="rounded-xl border border-border bg-card p-5 space-y-4">
          <div>
            <label className="text-xs text-muted-foreground">Truck</label>
            <VehiclePicker value={vehicleId} onChange={(id) => setVehicleId(id)} preferProjectScope="riyadh" placeholder="Pick a flatbed truck…" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-muted-foreground">Route distance (km)</label><input className={inputCls} value={km} onChange={(e) => setKm(e.target.value)} inputMode="decimal" placeholder="e.g. 450" /></div>
            <div><label className="text-xs text-muted-foreground">Offered price (SAR)</label><input className={inputCls} value={offered} onChange={(e) => setOffered(e.target.value)} inputMode="decimal" placeholder="what they pay" /></div>
          </div>
        </div>

        {!vehicleId || Number(km) <= 0 ? (
          <p className="text-center text-sm text-muted-foreground mt-8 flex items-center justify-center gap-2"><Calculator className="h-4 w-4" /> Pick a truck and enter the km to see the cost to serve.</p>
        ) : est ? (
          <div className="rounded-xl border border-border bg-card p-5 mt-6">
            <div className="grid grid-cols-2 gap-y-2 text-sm">
              <div className="text-muted-foreground">Diesel ({est.dieselSource}, {est.dieselConfidence})</div>
              <div className="text-end">{est.breakdown.dieselSar != null ? `${sar(est.breakdown.dieselSar)} SAR` : <span className="text-amber-500">no fuel data yet</span>}</div>
              <div className="text-muted-foreground">Driver ({est.breakdown.driverPerKmSar}/km × {est.km})</div>
              <div className="text-end">{sar(est.breakdown.driverSar)} SAR</div>
              <div className="text-muted-foreground">Wasika</div>
              <div className="text-end">{sar(est.breakdown.wasikaSar)} SAR</div>
              <div className="text-muted-foreground">Tasreeh</div>
              <div className="text-end">{sar(est.breakdown.tasreehSar)} SAR</div>
              <div className="font-medium border-t border-border pt-2 mt-1">Cost to serve</div>
              <div className="text-end font-medium border-t border-border pt-2 mt-1">{est.totalCostSar != null ? `${sar(est.totalCostSar)} SAR` : '—'}</div>
            </div>
            {est.dieselNote && <div className="text-xs text-amber-600 mt-2">{est.dieselNote}</div>}

            {est.offeredPriceSar != null && est.marginSar != null && (
              <div className={`mt-4 rounded-lg p-4 flex items-center justify-between ${est.worthIt ? 'bg-emerald-500/10' : 'bg-rose-500/10'}`}>
                <div className="flex items-center gap-2">
                  {est.worthIt ? <TrendingUp className="h-5 w-5 text-emerald-500" /> : <TrendingDown className="h-5 w-5 text-rose-500" />}
                  <span className="text-sm">{est.worthIt ? 'Worth taking' : 'Below cost'}</span>
                </div>
                <div className={`text-lg font-semibold ${est.worthIt ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {sar(est.marginSar)} SAR{est.marginPct != null ? ` (${est.marginPct}%)` : ''}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-center text-sm text-muted-foreground mt-8">{isFetching ? 'Calculating…' : 'No estimate.'}</p>
        )}
      </main>
    </div>
  );
}
